import {computed} from "vue";
import {Dayjs} from "dayjs";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {frequentationApi} from "@/container";
import {FrequentationWeek} from "@/core/interfaces/Frequentation";

/**
 * Composable to fetch the expected load of one week of the user's pharmacy.
 *
 * Keyed by pharmacy and by week: the colours follow the season, so each week
 * consulted is its own entry, and a workstation switching pharmacy must not
 * read the previous one's cache. The default SWR policy (24 h TTL, no
 * revalidation on focus) fits data the backend recomputes daily at most.
 *
 * @param weekStart The Monday of the week to load, null when nothing is to load.
 */
const useFrequentation = (weekStart: () => Dayjs | null) => {
    const organizationId = useOrganizationId();

    const key = computed(() => {
        const start = weekStart();

        if (!organizationId.value || !start) {
            return null;
        }

        return cacheKey(`organizations/${organizationId.value}/frequentation`, {week: start.format('YYYY-MM-DD')});
    });

    const {data, isLoading, isValidating, error} = useSwr<FrequentationWeek | null>(
        () => key.value,
        () => {
            const start = weekStart();

            return start ? frequentationApi().week(start.format('YYYY-MM-DD')) : Promise.resolve(null);
        }
    );

    const frequentation = computed(() => data.value ?? null);

    /**
     * Whether there is anything to overlay: an inactive pharmacy or one
     * without enough history gets nothing from the endpoint.
     */
    const hasData = computed(() => !!frequentation.value && frequentation.value.days.length > 0);

    return {
        frequentation,
        hasData,
        isLoading,
        /**
         * A request is in flight. Needed by whoever shows the week's dates:
         * on a key change the cache keeps the previous week's data until the
         * new one lands, and `isLoading` (data undefined) stays false.
         */
        isValidating,
        error
    };
};

export default useFrequentation;
